import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { computed, inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { take } from 'rxjs';
import { environment } from 'src/environments/environment';

type UserProfile = {
  email: string;
  username: string;
  is_admin: boolean;
};

type UserProfileResponse = {
  data: UserProfile;
};

@Injectable({
  providedIn: 'root',
})
export class LoginService {
  private http: HttpClient = inject(HttpClient);
  private profile: WritableSignal<UserProfile | null> = signal<UserProfile | null>(null);

  readonly isLogged: Signal<boolean> = computed(() => this.profile() !== null);
  readonly username: Signal<string | null> = computed(() => this.profile()?.username ?? null);
  readonly email: Signal<string | null> = computed(() => this.profile()?.email ?? null);
  readonly isAdmin: Signal<boolean> = computed(() => this.profile()?.is_admin ?? false);

  logout(): void {
    localStorage.clear();
    this.profile.set(null);
  }

  controlToken(): void {
    const url: URL = new URL(window.location.href);
    const callbackTokens: string[] = url.searchParams.getAll('token');
    const callbackToken: string | null =
      callbackTokens.filter((token: string) => token !== '').at(-1) ?? null;

    if (callbackTokens.length > 0) {
      url.searchParams.delete('token');
      window.history.replaceState({}, document.title, url.toString());
    }

    if (callbackToken) {
      localStorage.setItem('user_access_token', callbackToken);
      this.getProfile(callbackToken);
      return;
    }

    const localToken: string | null = localStorage.getItem('user_access_token');

    if (localToken) this.getProfile(localToken);
    else this.redirectToOauth();
  }

  getProfile(userAccessToken: string): void {
    const headers: HttpHeaders = new HttpHeaders({
      Authorization: `Bearer ${userAccessToken}`,
    });
    this.http
      .get<UserProfileResponse>(environment.api.client.me, { headers })
      .pipe(take(1))
      .subscribe((profile: UserProfileResponse) => {
        this.profile.set(profile.data);
      });
  }

  /**
   * Une methode qui permet de se connecter via l'OAuth de kinomap
   * sur le site login.kinomap.com/dev
   */
  redirectToOauth(): void {
    const redirectUri: URL = new URL(window.location.href);

    redirectUri.searchParams.delete('token');

    let params: HttpParams = new HttpParams()
      .set('client_id', '55') // le client ID du LEADERBOARD sur login.kinomap.com
      .set('redirect_uri', redirectUri.toString())
      .set('state', Math.random().toString(36).substring(2, 15));

    if (redirectUri.hostname === 'leaderboard.kinomap.com') {
      params = params.set('client_id', '55'); // le client ID du site-web sur beta.kinomap.com
    }

    const oauthUrl: string = `${environment.oauth.url}?${params.toString()}`;
    window.location.href = oauthUrl;
  }
}
